import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsPreviewMode } from './useIsPreviewMode';
import { appendPreviewQuery } from '../utils/appointmentStageRouting';

/**
 * usePreviewAwareNavigate
 *
 * A drop-in replacement for react-router's `useNavigate` that keeps a page in
 * "preview" (read-only) mode as the user moves between its steps.
 *
 * The problem it solves
 * ---------------------
 * Single-page wizards (History, Observation) switch "steps" with internal state,
 * so the `?preview=true` flag in the URL — and the read-only banner that depends
 * on it — naturally stays put. The Diagnostic Decision and Post-Assessment
 * Discussion wizards are different: each step is its OWN route, navigated to with
 * `navigate('/.../next-step')`. A plain navigate would drop the `?preview=true`
 * query string, so a clinician previewing the module would silently "fall out"
 * of preview after the first step.
 *
 * What it does
 * ------------
 * If the current URL has `?preview=true`, every string destination is rewritten
 * to carry the same flag (via `appendPreviewQuery`, which preserves any other
 * query params). When NOT previewing, the destination is passed through
 * untouched, so normal editing navigation is completely unaffected. Non-string
 * destinations (e.g. `navigate(-1)` for "go back") are always passed through
 * unchanged, since there is no URL to annotate. Any navigate options such as
 * `{ replace: true }` are forwarded as-is.
 *
 * Why a hook (and not threading a flag through the route helpers)
 * --------------------------------------------------------------
 * The route-building helpers (e.g. getAutismDDStepRoutes) are pure and shared by
 * routes.js, the sidebars and the unit tests; making them preview-aware would
 * leak view concerns into routing config. Centralising it here means each page
 * only swaps `useNavigate()` for `usePreviewAwareNavigate()` — a one-line change
 * — and every navigation in that page becomes preview-preserving automatically.
 *
 * The returned function has a stable identity (memoised on the navigate fn and
 * the preview flag) so it is safe to list in `useCallback`/`useMemo`/`useEffect`
 * dependency arrays without causing re-render churn.
 *
 * Any extra arguments (e.g. the options object `{ replace: true }`) are
 * forwarded verbatim via a rest spread, which also preserves the exact call
 * arity — `navigate(path)` stays a one-argument call rather than becoming
 * `navigate(path, undefined)`.
 *
 * @returns {(to: string|number, ...rest: any[]) => void} a navigate function
 */
export function usePreviewAwareNavigate() {
  const navigate = useNavigate();
  const isPreview = useIsPreviewMode();

  return useCallback(
    (to, ...rest) => {
      const destination = isPreview && typeof to === 'string' ? appendPreviewQuery(to) : to;
      navigate(destination, ...rest);
    },
    [navigate, isPreview],
  );
}
